// UserReviewsSection.jsx (JSX)
import { useEffect, useMemo, useState } from "react";
import {
  List,
  Button,
  Modal,
  Form,
  Select,
  Rate,
  Input,
  Tag,
  Space,
  Typography,
  message,
  Empty,
  Skeleton,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import routes from "../../../requests/routes";
import axios from "../../../requests/axios";
import classes from "./UserProfileReviews.module.scss";
import ClampText from "../../../generic components/TextTruncate/TextTruncate";

const { Text, Title } = Typography;
const { TextArea } = Input;

const ENDPOINTS = {
  // tweak these to your routes setup
  getUserReviews: (userId) => routes?.getReviews + `/${userId}`,
  getPrograms: routes.getPrograms,
  createReview: routes.addReview,
};

export const UserProfileReviews = ({ userId }) => {
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  // fetch user's reviews
  const fetchReviews = async () => {
    try {
      setLoadingReviews(true);
      const { data } = await axios.get(ENDPOINTS.getUserReviews(userId));
      // Accepts either {reviews: []} or []
      const list = Array.isArray(data) ? data : data?.reviews || [];
      setReviews(list);
    } catch (e) {
      console.error(e);
      message.error("Failed to load reviews");
      setReviews([]);
    } finally {
      setLoadingReviews(false);
    }
  };

  // fetch programs to select from
  const fetchPrograms = async () => {
    try {
      setLoadingPrograms(true);
      const { data } = await axios.get(ENDPOINTS.getPrograms, {
        params: { isPublished: true },
      });
      const list = Array.isArray(data) ? data : data?.programs || [];
      setPrograms(list);
    } catch (e) {
      console.error(e);
      message.error("Failed to load programs");
      setPrograms([]);
    } finally {
      setLoadingPrograms(false);
    }
  };

  useEffect(() => {
    fetchReviews();
    fetchPrograms();
  }, []);

  // Map of programId -> program
  const programById = useMemo(() => {
    const m = {};
    programs.forEach((p) => (m[p._id] = p));
    return m;
  }, [programs]);

  // prevent duplicate reviews on same program (disable those options)
  const alreadyReviewedProgramIds = useMemo(() => {
    const s = new Set();
    reviews.forEach((r) => {
      const id = r?.program?._id || r?.program; // populated or raw id
      if (id) s.add(String(id));
    });
    return s;
  }, [reviews]);

  const programOptions = useMemo(
    () =>
      programs.map((p) => ({
        label: p.name,
        value: p._id,
        disabled: alreadyReviewedProgramIds.has(String(p._id)),
      })),
    [programs, alreadyReviewedProgramIds]
  );

  const openModal = () => {
    form.resetFields();
    setOpen(true);
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      const payload = {
        user: userId,
        program: values.program,
        rating: values.rating,
        text: values.text?.trim(),
      };
      const { data } = await axios.post(ENDPOINTS.createReview, payload);
      const created = data?.review || data; // accept either shape

      // optimistic update
      setReviews((prev) => [created, ...prev]);
      message.success("Review submitted! It may need admin approval.");
      setOpen(false);
    } catch (e) {
      console.error(e);
      message.error(e?.response?.data?.message || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.reviews}>
        {loadingReviews ? (
          <Skeleton active />
        ) : reviews.length === 0 ? (
          <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
            <div
              onClick={openModal}
              className={`${classes.review} ${classes.add_review}`}
              style={{ maxWidth: "150px" }}
            >
              <PlusOutlined />
              <span style={{ fontSize: "2rem" }}>Add a Review</span>
            </div>
            <Empty
              style={{ flex: 1, marginInline: "auto" }}
              description="No reviews yet"
            />
          </div>
        ) : (
          <>
            <div
              onClick={openModal}
              className={`${classes.review} ${classes.add_review}`}
            >
              <PlusOutlined />
              <span style={{ fontSize: "2rem" }}>Add a Review</span>
            </div>
            {reviews.map((review) => {
              const prog =
                review.program && typeof review.program === "object"
                  ? review.program
                  : programById[review.program];
              const progName = prog?.name || "Program";
              const when = review.createdAt
                ? new Date(review.createdAt).toLocaleString()
                : "";

              return (
                <div className={classes.review}>
                  <Text strong>{progName}</Text>
                  <Text type="secondary">{when}</Text>
                  <Space>
                    <Rate disabled value={Number(review.rating) || 0} />
                  </Space>
                  <Space>
                    {review.isPublished === null ? (
                      <Tag color="gray">Pending Approval</Tag>
                    ) : review.isPublished ? (
                      <Tag color="green">Puplished</Tag>
                    ) : (
                      <Tag color="red">Unpublished</Tag>
                    )}
                    {/* {prog && <Tag color="blue">{progName}</Tag>} */}
                  </Space>
                  <Text strong>Review:</Text>
                  <ClampText text={review.text} lines={1} />
                </div>
              );
            })}
          </>
        )}
      </div>

      <Modal
        open={open}
        title="Write a review"
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        okText="Submit review"
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ rating: 0, program: undefined, text: "" }}
        >
          <Form.Item
            label="Program"
            name="program"
            rules={[{ required: true, message: "Please select a program" }]}
          >
            <Select
              showSearch
              placeholder="Select a program"
              options={programOptions}
              loading={loadingPrograms}
              filterOption={(input, option) =>
                (option?.label || "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            label="Rating"
            name="rating"
            rules={[{ required: true, message: "Please rate this program" }]}
          >
            <Rate />
          </Form.Item>

          <Form.Item
            label="Review"
            name="text"
            rules={[
              { required: true, message: "Please write a short review" },
              { min: 10, message: "Please write at least 10 characters" },
              { max: 1000, message: "Keep it under 1000 characters" },
            ]}
          >
            <TextArea
              rows={5}
              showCount
              maxLength={1000}
              placeholder="Share your experience…"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserProfileReviews;
